import React, { useEffect, useRef, useState } from 'react';
import Form, { Actions, Conditional, Radio, RegexValidator, RequiredValidator, TextField } from '../../../Components/Form';
import API from '../../../Services/API';
import Loading from '../../../Components/Loading';
import Icon from '../../../Components/Icons/Icon';
import { useDebounce } from '../../../Services/helpers';
import { useToasts } from '../../../Components/Toasts';
import { useOnboarding } from '../OnboardingProvider';
import { slugify, SUBDOMAIN_REGEX } from '../slugify';
import StepShell, { StepActions, nextSubmit } from '../Components/StepShell';

const WebsiteStep = () => {
    const {draft, updateStepData, next, back} = useOnboarding();
    const {showError} = useToasts();
    const [availability, setAvailability] = useState(null);
    const [snapshot, setSnapshot] = useState(draft.data.website ?? {});

    // Suggest a subdomain from the business name the first time around
    const initialData = {
        ...draft.data.website,
        subdomain: draft.data.website?.subdomain || slugify(draft.data.businessName?.name) || null,
    };

    const submit = (data) => {
        if (data.choice === 'getsoapy' && availability?.available === false) {
            showError('That subdomain isn\'t available - please pick another.');
            return;
        }

        updateStepData('website', data);
        next();
    };

    return <StepShell
        title="Do you have a website?"
        intro="Every getSoapy account includes a free website with online quotes and payments built in."
    >
        <Form
            data={initialData}
            onChange={(data) => {
                setSnapshot(data);
                updateStepData('website', data);
            }}
            onSubmit={submit}
        >
            <Radio name="choice" label="Website" layout="List" validators={[RequiredValidator]} options={[
                {value: 'getsoapy', label: 'I\'d like a free one'},
                {value: 'external', label: 'I already have one'},
                {value: 'later', label: 'I\'ll set this up later'},
            ]} />

            <Conditional condition={(data) => data.choice === 'getsoapy'}>
                <TextField
                    name="subdomain"
                    label="Choose your address"
                    prefix="//"
                    suffix=".gspy.com"
                    validators={[
                        RequiredValidator,
                        RegexValidator(SUBDOMAIN_REGEX, 'Use only lowercase letters and hyphens, starting and ending with a letter'),
                    ]}
                />
                <SubdomainAvailability
                    subdomain={snapshot.choice === 'getsoapy' ? snapshot.subdomain : null}
                    onResult={setAvailability}
                />
            </Conditional>

            <Conditional condition={(data) => data.choice === 'external'}>
                <TextField
                    name="domain"
                    label="Your website address"
                    prefix="https://"
                    validators={[
                        RequiredValidator,
                        RegexValidator(/^[a-z0-9][.a-z0-9-]{0,61}[a-z0-9]\.[a-z]{2,}$/i, 'Please enter a valid domain name'),
                    ]}
                />
            </Conditional>

            <StepActions>
                <Actions submit={nextSubmit} cancel={{label: 'Back', onClick: back}} />
            </StepActions>
        </Form>
    </StepShell>;
};

const SubdomainAvailability = ({subdomain, onResult}) => {
    const debounced = useDebounce(subdomain, 500);
    const [state, setState] = useState(null);
    const requestRef = useRef(0);

    useEffect(() => {
        onResult(state);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    useEffect(() => {
        if (!debounced || !SUBDOMAIN_REGEX.test(debounced)) {
            setState(null);
            return;
        }

        const requestId = ++requestRef.current;
        setState({checking: true});

        API.get('/website/subdomain-availability', {subdomain: debounced})
            .then((response) => {
                if (requestRef.current === requestId) {
                    setState({...response, subdomain: debounced});
                }
            })
            .catch(() => {
                if (requestRef.current === requestId) {
                    // Don't block the wizard on a failed check - the backend
                    // re-checks at registration and degrades gracefully
                    setState(null);
                }
            });
    }, [debounced]);

    if (!state) {
        return null;
    }

    if (state.checking) {
        return <p className="-mt-2 mb-3 text-sm text-muted flex items-center gap-x-2">
            <Loading /> Checking availability…
        </p>;
    }

    if (state.available) {
        return <p className="-mt-2 mb-3 text-sm text-green-600 fill-green-600 flex items-center gap-x-1">
            <Icon icon="Check" theme="solid" /> {state.subdomain} is available
        </p>;
    }

    return <p className="-mt-2 mb-3 text-sm text-red-500">
        {state.reason === 'taken' && <>Sorry, {state.subdomain}.getsoapy.com is taken — try {state.subdomain}-cleaning or {state.subdomain}-services.</>}
        {state.reason !== 'taken' && <>That subdomain can't be used — try something like {slugify(state.subdomain + ' cleaning') || 'my-business'}.</>}
    </p>;
};

export default WebsiteStep;
